import store from '../store';
import { toast } from 'react-toastify';
import { api } from '../config';
import { forceLogout, getSessionEndCode } from './sessionManager';

const PING_INTERVAL = 5000;

let _pingIntervalId = null;
const _waitingFetches = [];
let _isRetrying = false;
const TOAST_ID = 'api-disconnected';

const originalFetch = window.fetch;

function getToken() {
  const state = store.getState();
  return state.Login?.user?.token;
}

async function pingServer() {
  const token = getToken();
  if (!token) return false;
  const res = await originalFetch(`${api.API_URL}/account/my-profile`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  });
  return res.ok;
}

function startGlobalPing() {
  if (_pingIntervalId) return;

  _pingIntervalId = setInterval(async () => {
    try {
      const online = await pingServer();
      if (!online) return;

      clearInterval(_pingIntervalId);
      _pingIntervalId = null;
      const entries = _waitingFetches.splice(0);
      if (entries.length === 0) return;

      _isRetrying = true;
      Promise.allSettled(entries.map(e => e.resolve())).finally(() => {
        _isRetrying = false;
      });
    } catch {
      // ping failed, continue interval
    }
  }, PING_INTERVAL);
}

window.fetch = async function (...args) {
  try {
    const response = await originalFetch(...args);

    // 401 berkode sesi dari BE → force logout (401 tanpa kode dibiarkan ke pemanggil).
    if (response.status === 401) {
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      const sessionCode = getSessionEndCode(401, body);
      if (sessionCode) forceLogout(sessionCode);
    }

    if (toast.isActive(TOAST_ID)) {
      toast.update(TOAST_ID, {
        render: 'Connection restored.',
        type: 'success',
        isLoading: false,
      });
      setTimeout(() => toast.dismiss(TOAST_ID), 2000);
    }

    return response;
  } catch (error) {
    if (!(error instanceof TypeError && error.message === 'Failed to fetch')) {
      throw error;
    }

    if (_isRetrying) {
      return new Promise((resolve, reject) => {
        _waitingFetches.push({
          reject,
          resolve: async () => {
            try {
              const retryResponse = await originalFetch(...args);
              if (toast.isActive(TOAST_ID)) {
                toast.update(TOAST_ID, {
                  render: 'Connection restored. Reloading data...',
                  type: 'success',
                  isLoading: false,
                });
                setTimeout(() => toast.dismiss(TOAST_ID), 2000);
              }
              resolve(retryResponse);
            } catch (retryError) {
              reject(retryError);
            }
          },
        });
        if (!_pingIntervalId) startGlobalPing();
      });
    }

    if (!toast.isActive(TOAST_ID)) {
      toast(
        <div>
          <span
            className='spinner-border spinner-border-sm me-2'
            role='status'
          />
          Cannot reach the server. Retrying automatically...
        </div>,
        {
          toastId: TOAST_ID,
          position: 'top-right',
          hideProgressBar: true,
          closeOnClick: false,
          closeButton: false,
          className: 'hy-toast hy-toast--warning',
          autoClose: false,
        }
      );
    }

    return new Promise((resolve, reject) => {
      let cancelled = false;
      const onBeforeUnload = () => {
        cancelled = true;
      };
      window.addEventListener('beforeunload', onBeforeUnload);
      const entry = {
        reject,
        resolve: async () => {
          window.removeEventListener('beforeunload', onBeforeUnload);
          if (cancelled) return;
          if (toast.isActive(TOAST_ID)) {
            toast.update(TOAST_ID, {
              render: 'Connection restored. Reloading data...',
              type: 'success',
              isLoading: false,
              autoClose: false,
            });
            setTimeout(() => toast.dismiss(TOAST_ID), 2000);
          }
          try {
            const retryResponse = await originalFetch(...args);
            resolve(retryResponse);
          } catch (retryError) {
            reject(retryError);
          }
        },
      };

      _waitingFetches.push(entry);
      startGlobalPing();
    });
  }
};
